import { useState, useEffect } from "react";
import { api, errMsg, uploadVideoChunked, mediaUrl } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { CourseThumb, ThumbArt, hashIdx } from "../../lib/art";
import { VideoPicker } from "../../components/VideoPicker";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const emptyCourse = { title: "", subtitle: "", description: "", section: "", level: "Beginner", includes_assets: false, includes_assets_text: "Complete project files & assets", language: "English", instructor: "Jalol M.", bestseller: false, video: "", price: 0, sale_price: null, sale_ends_at: null, thumbnail: "", bullets: [], requirements: [], curriculum: [], published: true };
const uid = () => crypto.randomUUID();

export default function AdminCourses() {
  const { sections, refreshSections } = useStore();
  const [courses, setCourses] = useState([]);
  const [edit, setEdit] = useState(null);
  const [secEdit, setSecEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [delText, setDelText] = useState("");
  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const load = () => api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async () => {
    await api.delete(`/admin/courses/${del.id}`);
    toast.success("Course deleted");
    setDel(null); setDelText("");
    load(); refreshSections();
  };

  const removeSection = async (id) => {
    if (!window.confirm("Delete this whole section and all its courses?")) return;
    await api.delete(`/admin/sections/${id}`);
    toast.success("Section deleted");
    load(); refreshSections();
  };

  const saveSection = async (e) => {
    e.preventDefault();
    const body = { title: secEdit.title, tagline: secEdit.tagline || "", icon: secEdit.icon || "📦", color: secEdit.color || "bp", enabled: secEdit.enabled !== false };
    try {
      if (secEdit.id) await api.put(`/admin/sections/${secEdit.id}`, body);
      else await api.post("/admin/sections", body);
      toast.success("Section saved ✓");
      setSecEdit(null); refreshSections();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  if (edit) return <CourseEditor initial={edit} sections={sections} onDone={() => { setEdit(null); load(); refreshSections(); }} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-bold text-xl" data-testid="admin-courses-title">SECTIONS & COURSES</h1>
      </div>

      <div className="forge-card p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-sm" style={{ letterSpacing: 1 }}>SECTIONS</h2>
          <button data-testid="new-section-btn" onClick={() => setSecEdit({ title: "", tagline: "", icon: "📦", color: "bp" })} className="btn-gold !py-1 !px-3 text-xs">+ ADD SECTION</button>
        </div>
        {secEdit && (
          <form onSubmit={saveSection} className="grid sm:grid-cols-5 gap-2 mb-3 p-3 rounded border border-[var(--line)]" data-testid="section-form">
            <input data-testid="section-form-title" required placeholder="Title" value={secEdit.title} onChange={(e) => setSecEdit({ ...secEdit, title: e.target.value })} className="input-dark text-sm" />
            <input data-testid="section-form-tagline" placeholder="Tagline" value={secEdit.tagline} onChange={(e) => setSecEdit({ ...secEdit, tagline: e.target.value })} className="input-dark text-sm sm:col-span-2" />
            <input placeholder="Icon" maxLength={4} value={secEdit.icon} onChange={(e) => setSecEdit({ ...secEdit, icon: e.target.value })} className="input-dark text-sm" />
            <div className="flex gap-2">
              <select value={secEdit.color} onChange={(e) => setSecEdit({ ...secEdit, color: e.target.value })} className="input-dark text-sm">
                <option value="bp">Blueprint Blue</option><option value="land">Landscape Green</option>
              </select>
              <button className="btn-primary !py-1 text-xs" data-testid="section-form-save">SAVE</button>
              <button type="button" onClick={() => setSecEdit(null)} className="btn-ghost !py-1 text-xs">✕</button>
            </div>
          </form>
        )}
        <div className="space-y-1">
          {sections.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-[var(--panel2)] text-sm" data-testid={`admin-section-${s.id}`}>
              <span>{s.icon}</span>
              <span className="font-medium">{s.title}</span>
              <span className="text-xs" style={{ color: "var(--muted)" }}>{s.tagline}</span>
              <span className="ml-auto text-xs" style={{ color: "var(--muted)" }}>{s.counts?.total ?? 0} courses</span>
              {s.id === "blueprints" ? (
                <span className="text-xs" style={{ color: "var(--gold)" }} title="Always visible">🔒 always on</span>
              ) : (
                <button data-testid={`toggle-section-${s.id}`}
                  onClick={async () => {
                    try {
                      await api.put(`/admin/sections/${s.id}`, { title: s.title, tagline: s.tagline || "", icon: s.icon, color: s.color, enabled: s.enabled === false });
                      toast.success(s.enabled === false ? `${s.title} is now visible` : `${s.title} hidden from the site`);
                      refreshSections();
                    } catch (e) { toast.error(errMsg(e)); }
                  }}
                  className="text-xs font-display px-2 py-0.5 rounded border"
                  style={s.enabled === false ? { color: "var(--muted)", borderColor: "var(--line)" } : { color: "var(--land)", borderColor: "rgba(82,210,115,.4)" }}>
                  {s.enabled === false ? "OFF" : "ON"}
                </button>
              )}
              <button data-testid={`edit-section-${s.id}`} onClick={() => setSecEdit({ ...s })} className="p-1 hover:text-[var(--bp)]" style={{ color: "var(--muted)" }}><Pencil size={14} /></button>
              {s.id !== "blueprints" && (
                <button data-testid={`delete-section-${s.id}`} onClick={() => removeSection(s.id)} className="p-1 hover:text-red-400" style={{ color: "var(--muted)" }}><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-sm" style={{ letterSpacing: 1 }}>COURSES</h2>
        <button data-testid="new-course-btn" onClick={() => setEdit({ ...emptyCourse, section: sections[0]?.id || "" })} className="btn-primary !py-1.5 text-sm flex items-center gap-1"><Plus size={15} /> NEW COURSE</button>
      </div>
      <div className="space-y-2">
        {courses.map((c) => (
          <div key={c.id} className="forge-card flex items-center gap-4 p-3" data-testid={`admin-course-${c.id}`}>
            <div className="w-20 h-12 rounded overflow-hidden relative flex-none"><CourseThumb course={c} color={secMap[c.section]?.color} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.title} {!c.published && <span className="text-xs" style={{ color: "var(--gold)" }}>(draft)</span>} {c.bestseller && <span className="text-xs" style={{ color: "var(--gold)" }}>★</span>}</p>
              <p className="text-xs" style={{ color: "var(--muted)" }}>{secMap[c.section]?.title || c.section} · {c.level} · ${c.price}{c.sale_price != null && ` → $${c.sale_price}`} · {c.students_count} students</p>
            </div>
            <button data-testid={`edit-course-${c.id}`} onClick={() => setEdit(c)} className="p-2 hover:text-[var(--bp)]" style={{ color: "var(--muted)" }}><Pencil size={16} /></button>
            <button data-testid={`delete-course-${c.id}`} onClick={() => { setDel(c); setDelText(""); }} className="p-2 hover:text-red-400" style={{ color: "var(--muted)" }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {del && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.75)" }} data-testid="delete-course-modal">
          <div className="forge-card w-full max-w-md p-6" style={{ border: "1px solid rgba(248,113,113,.4)" }}>
            <h3 className="font-display font-bold text-sm mb-3" style={{ color: "#f87171", letterSpacing: 1 }}>DELETE COURSE — THIS CANNOT BE UNDONE</h3>
            <p className="text-sm mb-2">You are about to permanently delete:</p>
            <p className="text-sm font-bold mb-3" style={{ color: "var(--gold)" }}>{del.title}</p>
            <ul className="text-xs mb-4 space-y-1" style={{ color: "var(--muted)" }}>
              <li>• All {del.students_count || 0} student enrollment{del.students_count === 1 ? "" : "s"} will be removed — students lose access</li>
              <li>• Curriculum, reviews and Q&A for this course are gone for good</li>
              <li>• Videos stay safe in your Storage / Cloudflare R2 (not deleted)</li>
            </ul>
            <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>Type <b style={{ color: "#f87171" }}>DELETE</b> to confirm:</p>
            <input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="DELETE" autoFocus
              className="input-dark text-sm w-full mb-4" data-testid="delete-course-confirm-input" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setDel(null); setDelText(""); }} className="btn-ghost !py-1.5 text-sm" data-testid="delete-course-cancel-btn">CANCEL</button>
              <button onClick={remove} disabled={delText.trim().toUpperCase() !== "DELETE"} data-testid="delete-course-confirm-btn"
                className="!py-1.5 text-sm px-4 rounded font-display font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#7f1d1d", color: "#fecaca", border: "1px solid #f87171" }}>DELETE FOREVER</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CourseEditor({ initial, sections, onDone }) {
  const [f, setF] = useState({ ...initial, bullets: initial.bullets || [], requirements: initial.requirements || [], curriculum: initial.curriculum || [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState({});
  const [picker, setPicker] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const probeDuration = (url) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    const done = (sec) => { v.removeAttribute("src"); v.load(); resolve(sec); };
    const t = setTimeout(() => done(null), 15000);
    v.onloadedmetadata = () => { clearTimeout(t); done(v.duration); };
    v.onerror = () => { clearTimeout(t); done(null); };
    v.src = mediaUrl(url);
  });

  const isProbeable = (url) => url.includes("/api/media/") || /\.(mp4|webm|mov|m4v|mkv)(\?|$)/i.test(url);

  const autoDuration = async (si, li, url) => {
    if (!url || !isProbeable(url)) return;
    const sec = await probeDuration(url);
    if (!sec || !isFinite(sec)) return;
    const secs = Math.round(sec);
    const mins = Math.max(1, Math.round(secs / 60));
    setF((prev) => ({ ...prev, curriculum: prev.curriculum.map((s, i) => i !== si ? s : { ...s, lectures: s.lectures.map((l, j) => j !== li ? l : { ...l, duration_min: mins, duration_sec: secs }) }) }));
    toast.success(`Duration set to ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")} from the video`);
  };

  const fmtDur = (l) => {
    const secs = l.duration_sec ?? (l.duration_min || 0) * 60;
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  };

  const setDur = (si, li, val) => {
    const m = val.trim().match(/^(\d+):([0-5]?\d)$/);
    const secs = m ? parseInt(m[1]) * 60 + parseInt(m[2]) : (parseInt(val) || 0) * 60;
    updLec(si, li, { duration_sec: secs, duration_min: secs > 0 ? Math.max(1, Math.round(secs / 60)) : 0 });
  };

  const uploadVideo = async (e, si, li) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const key = si === "trailer" ? "trailer" : `${si}-${li}`;
    setUploading((u) => ({ ...u, [key]: 0 }));
    try {
      const data = await uploadVideoChunked(file, (pct) => setUploading((u) => ({ ...u, [key]: pct })));
      if (key === "trailer") {
        setF((prev) => ({ ...prev, video: data.url }));
        toast.success("Trailer uploaded ✓");
      } else {
        setF((prev) => ({ ...prev, curriculum: prev.curriculum.map((s, i) => i !== si ? s : { ...s, lectures: s.lectures.map((l, j) => j !== li ? l : { ...l, video_url: data.url }) }) }));
        toast.success("Video uploaded ✓ — protected playback enabled");
        autoDuration(si, li, data.url);
      }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading((u) => { const n = { ...u }; delete n[key]; return n; }); }
  };

  const uploadTrailerThumb = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !f.video.includes("/api/media/")) return;
    const fid = f.video.split("/api/media/")[1].split("?")[0].split("/")[0];
    setUploading((u) => ({ ...u, trailerThumb: 0 }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/media/${fid}/thumbnail`, fd);
      toast.success("Trailer thumbnail set ✓ — shown on the course page preview");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading((u) => { const n = { ...u }; delete n.trailerThumb; return n; }); }
  };

  const uploadCourseThumb = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !f.id) return;
    setUploading((u) => ({ ...u, courseThumb: 0 }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/courses/${f.id}/thumbnail`, fd);
      set("thumbnail", r.data.thumbnail);
      toast.success("Course thumbnail updated ✓ — shown on all course cards");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading((u) => { const n = { ...u }; delete n.courseThumb; return n; }); }
  };

  const removeCourseThumb = async () => {
    try {
      await api.delete(`/admin/courses/${f.id}/thumbnail`);
      set("thumbnail", "");
      toast.success("Back to the generated artwork");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const save = async () => {
    setBusy(true);
    const body = { ...f, price: parseFloat(f.price) || 0, sale_price: f.sale_price === "" || f.sale_price == null ? null : parseFloat(f.sale_price), sale_ends_at: f.sale_ends_at || null };
    try {
      if (f.id) await api.put(`/admin/courses/${f.id}`, body);
      else await api.post("/admin/courses", body);
      toast.success("Course saved ✓");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const listField = (key, label, testid) => (
    <div>
      <label className="text-xs" style={{ color: "var(--muted)" }}>{label} (one per line)</label>
      <textarea data-testid={testid} value={f[key].join("\n")} onChange={(e) => set(key, e.target.value.split("\n"))}
        onBlur={(e) => set(key, e.target.value.split("\n").filter((x) => x.trim()))} className="input-dark text-sm mt-1" rows={3} />
    </div>
  );

  const updSec = (si, patch) => set("curriculum", f.curriculum.map((s, i) => i === si ? { ...s, ...patch } : s));
  const updLec = (si, li, patch) => updSec(si, { lectures: f.curriculum[si].lectures.map((l, i) => i === li ? { ...l, ...patch } : l) });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-xl">{f.id ? "EDIT COURSE" : "NEW COURSE"}</h1>
        <div className="flex gap-2">
          <button onClick={onDone} className="btn-ghost !py-1.5 text-sm" data-testid="editor-cancel-btn">CANCEL</button>
          <button onClick={save} disabled={busy} className="btn-primary !py-1.5 text-sm" data-testid="editor-save-btn">{busy ? "SAVING…" : "SAVE"}</button>
        </div>
        {picker && (
          <VideoPicker onClose={() => setPicker(null)}
            onPick={(url) => {
              updLec(picker.si, picker.li, { video_url: url });
              autoDuration(picker.si, picker.li, url);
              setPicker(null);
              toast.success("Video linked ✓ — protected playback enabled");
            }} />
        )}
      </div>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="editor-title" placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} className="input-dark text-sm sm:col-span-2" />
          <input data-testid="editor-subtitle" placeholder="Subtitle" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="input-dark text-sm sm:col-span-2" />
          {f.id ? (
            <div className="flex items-center gap-3 sm:col-span-2">
              <div className="w-24 h-14 rounded overflow-hidden relative flex-none" style={{ border: "1px solid var(--line)" }}>
                <CourseThumb course={f} color={sections.find((s) => s.id === f.section)?.color} />
              </div>
              <label className="text-xs cursor-pointer px-3 py-2 rounded border" data-testid="upload-course-thumb-btn"
                style={{ color: "var(--gold)", borderColor: "rgba(245,192,78,.4)" }}>
                {uploading.courseThumb != null ? "⬆ Uploading…" : "⬆ Upload course thumbnail (JPG/PNG/WebP, HD or 4K)"}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                  disabled={uploading.courseThumb != null} onChange={uploadCourseThumb} />
              </label>
              {f.thumbnail && (
                <button type="button" onClick={removeCourseThumb} data-testid="remove-course-thumb-btn"
                  className="text-xs hover:text-red-400" style={{ color: "var(--muted)" }}>✕ Remove (use generated art)</button>
              )}
            </div>
          ) : (
            <p className="text-xs sm:col-span-2" style={{ color: "var(--muted)" }}>Save the course first, then reopen it to upload a custom thumbnail.</p>
          )}
          <select data-testid="editor-section" value={f.section} onChange={(e) => set("section", e.target.value)} className="input-dark text-sm">
            {sections.map((s) => <option key={s.id} value={s.id}>{s.icon} {s.title}</option>)}
          </select>
          <select data-testid="editor-level" value={f.level} onChange={(e) => set("level", e.target.value)} className="input-dark text-sm">
            {["Beginner", "Beginner–Intermediate", "Intermediate", "Advanced"].map((l) => <option key={l}>{l}</option>)}
          </select>
          <input data-testid="editor-instructor" placeholder="Instructor" value={f.instructor} onChange={(e) => set("instructor", e.target.value)} className="input-dark text-sm" />
          <input data-testid="editor-language" placeholder="Language" value={f.language} onChange={(e) => set("language", e.target.value)} className="input-dark text-sm" />
          <input data-testid="editor-price" type="number" step="0.01" placeholder="Price USD (0 = free)" value={f.price} onChange={(e) => set("price", e.target.value)} className="input-dark text-sm" />
          <input data-testid="editor-sale-price" type="number" step="0.01" placeholder="Sale price (empty = no sale)" value={f.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} className="input-dark text-sm" />
          <div className="sm:col-span-2">
            <label className="text-xs" style={{ color: "var(--muted)" }}>Sale ends (optional, for time-limited discounts)</label>
            <input data-testid="editor-sale-ends" type="datetime-local" value={f.sale_ends_at ? f.sale_ends_at.slice(0, 16) : ""}
              onChange={(e) => set("sale_ends_at", e.target.value ? new Date(e.target.value).toISOString() : null)} className="input-dark text-sm mt-1" />
          </div>
          <input data-testid="editor-video" placeholder="Trailer video URL (YouTube embed, mp4, or upload →)" value={f.video} onChange={(e) => set("video", e.target.value)} className="input-dark text-sm" />
          <label className="text-xs cursor-pointer flex items-center justify-center px-3 py-2 rounded border" data-testid="upload-trailer-btn"
            style={{ color: "var(--gold)", borderColor: "rgba(245,192,78,.4)" }}>
            {uploading.trailer != null ? `⬆ Uploading trailer ${uploading.trailer}%` : "⬆ Upload trailer video"}
            <input type="file" accept="video/mp4,video/webm,video/quicktime,video/x-matroska" hidden
              disabled={uploading.trailer != null} onChange={(e) => uploadVideo(e, "trailer")} />
          </label>
          {f.video.includes("/api/media/") && (
            <label className="text-xs cursor-pointer flex items-center justify-center px-3 py-2 rounded border" data-testid="upload-trailer-thumb-btn"
              style={{ color: "var(--bp)", borderColor: "rgba(56,182,255,.35)" }}>
              {uploading.trailerThumb != null ? "⬆ Uploading thumbnail…" : "⬆ Upload trailer thumbnail (JPG/PNG/WebP, HD or 4K)"}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                disabled={uploading.trailerThumb != null} onChange={uploadTrailerThumb} />
            </label>
          )}
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input data-testid="editor-includes-assets" type="checkbox" checked={!!f.includes_assets} onChange={(e) => set("includes_assets", e.target.checked)} /> Show "included files" line on course page
          </label>
          {f.includes_assets && (
            <input data-testid="editor-includes-assets-text" placeholder='e.g. "Fab assets included" or "Complete project files & assets"'
              value={f.includes_assets_text ?? "Complete project files & assets"} onChange={(e) => set("includes_assets_text", e.target.value)} className="input-dark text-sm sm:col-span-2" />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="editor-bestseller" type="checkbox" checked={f.bestseller} onChange={(e) => set("bestseller", e.target.checked)} /> Bestseller badge
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="editor-published" type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} /> Published
          </label>
        </div>
        <textarea data-testid="editor-description" placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} className="input-dark text-sm" rows={4} />
        {listField("bullets", "What you'll learn", "editor-bullets")}
        {listField("requirements", "Requirements", "editor-requirements")}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display font-bold text-sm">CURRICULUM</h2>
            <button data-testid="add-section-btn" onClick={() => set("curriculum", [...f.curriculum, { id: uid(), title: "New Section", lectures: [] }])}
              className="btn-gold !py-1 !px-3 text-xs">+ CURRICULUM SECTION</button>
          </div>
          <div className="space-y-3">
            {f.curriculum.map((s, si) => (
              <div key={s.id} className="forge-card p-3">
                <div className="flex gap-2 items-center mb-2">
                  <input value={s.title} onChange={(e) => updSec(si, { title: e.target.value })} className="input-dark !py-1.5 text-sm flex-1" data-testid={`section-title-${si}`} />
                  <span className="text-xs font-display whitespace-nowrap" style={{ color: "var(--bp)" }} data-testid={`section-duration-${si}`}>
                    {(() => { const m = s.lectures.reduce((n, l) => n + (l.duration_min || 0), 0); const h = Math.floor(m / 60); return h ? `${h}h ${m % 60}m` : `${m}m`; })()} · {s.lectures.length} lect.
                  </span>
                  <button onClick={() => set("curriculum", f.curriculum.filter((_, i) => i !== si))} className="hover:text-red-400" style={{ color: "var(--muted)" }}><X size={16} /></button>
                </div>
                {s.lectures.map((l, li) => (
                  <div key={l.id} className="grid grid-cols-[1fr_70px_auto_auto] gap-2 items-center mb-1.5 pl-3">
                    <input value={l.title} onChange={(e) => updLec(si, li, { title: e.target.value })} placeholder="Lecture title" className="input-dark !py-1 text-xs" data-testid={`lecture-title-${si}-${li}`} />
                    <input key={`${l.id}-${l.duration_sec ?? l.duration_min}`} defaultValue={fmtDur(l)} onBlur={(e) => setDur(si, li, e.target.value)} title="Length as min:sec (e.g. 12:30) — auto-read from the video link" data-testid={`lecture-duration-${si}-${li}`} className="input-dark !py-1 text-xs text-center" />
                    <label className="flex items-center gap-1 text-xs" style={{ color: "var(--muted)" }}>
                      <input type="checkbox" checked={l.preview} onChange={(e) => updLec(si, li, { preview: e.target.checked })} data-testid={`lecture-preview-${si}-${li}`} />Preview
                    </label>
                    <button onClick={() => updSec(si, { lectures: s.lectures.filter((_, i) => i !== li) })} className="hover:text-red-400" style={{ color: "var(--muted)" }}><X size={14} /></button>
                    <div className="col-span-4 flex gap-2 items-center">
                      <input value={l.video_url} onChange={(e) => updLec(si, li, { video_url: e.target.value })} onBlur={(e) => autoDuration(si, li, e.target.value)} placeholder="Video URL (or upload →)" className="input-dark !py-1 text-xs flex-1" data-testid={`lecture-video-${si}-${li}`} />
                      <button type="button" onClick={() => setPicker({ si, li })} data-testid={`pick-video-btn-${si}-${li}`}
                        className="text-xs whitespace-nowrap px-2 py-1 rounded border" style={{ color: "var(--bp)", borderColor: "rgba(56,182,255,.4)" }}>
                        From storage
                      </button>
                      <label className="text-xs cursor-pointer whitespace-nowrap px-2 py-1 rounded border" data-testid={`upload-video-btn-${si}-${li}`}
                        style={{ color: "var(--gold)", borderColor: "rgba(245,192,78,.4)" }}>
                        {uploading[`${si}-${li}`] != null ? `⬆ ${uploading[`${si}-${li}`]}%` : "⬆ Upload video"}
                        <input type="file" accept="video/mp4,video/webm,video/quicktime,video/x-matroska" hidden
                          disabled={uploading[`${si}-${li}`] != null} onChange={(e) => uploadVideo(e, si, li)} />
                      </label>
                    </div>
                  </div>
                ))}
                <button data-testid={`add-lecture-btn-${si}`} onClick={() => updSec(si, { lectures: [...s.lectures, { id: uid(), title: "", duration_min: 5, video_url: "", preview: false }] })}
                  className="text-xs pl-3 mt-1" style={{ color: "var(--bp)" }}>+ Add lecture</button>
              </div>
            ))}
            <button data-testid="add-section-bottom-btn"
              onClick={() => set("curriculum", [...f.curriculum, { id: uid(), title: "New Section", lectures: [] }])}
              className="w-full py-3 rounded-xl text-xs font-display transition-colors"
              style={{ border: "2px dashed var(--line)", color: "var(--gold)", letterSpacing: 1 }}>
              + ADD CURRICULUM SECTION
            </button>
          </div>
        </div>
        {f.id && <Announcements courseId={f.id} />}
      </div>
    </div>
  );
}

function Announcements({ courseId }) {
  const [anns, setAnns] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get(`/courses/${courseId}/announcements`).then((r) => setAnns(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [courseId]);

  const post = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/announcements", { course_id: courseId, ...form });
      toast.success("Announcement posted — enrolled students will see it in the player");
      setForm({ title: "", body: "" }); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const remove = async (id) => {
    await api.delete(`/admin/announcements/${id}`);
    toast.success("Announcement deleted"); load();
  };

  return (
    <div className="mt-6">
      <h2 className="font-display font-bold text-sm mb-2">📢 ANNOUNCEMENTS <span className="text-xs font-normal" style={{ color: "var(--muted)" }}>— enrolled students see these in the course player</span></h2>
      <form onSubmit={post} className="forge-card p-3 space-y-2 mb-3" data-testid="announcement-form">
        <input data-testid="announcement-title" required placeholder="Announcement title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark text-sm" />
        <textarea data-testid="announcement-body" placeholder="Message (optional)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-dark text-sm" rows={2} />
        <button className="btn-outline !py-1.5 text-xs" data-testid="announcement-post-btn">POST ANNOUNCEMENT</button>
      </form>
      <div className="space-y-2">
        {anns.map((a) => (
          <div key={a.id} className="forge-card p-3 flex items-start gap-3 text-sm" data-testid={`admin-announcement-${a.id}`}>
            <div className="flex-1">
              <p className="font-semibold">{a.title} <span className="text-xs font-normal" style={{ color: "var(--muted)" }}>· {new Date(a.created_at).toLocaleDateString()}</span></p>
              {a.body && <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{a.body}</p>}
            </div>
            <button data-testid={`delete-announcement-${a.id}`} onClick={() => remove(a.id)} className="p-1 hover:text-red-400" style={{ color: "var(--muted)" }}><X size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
